import { IconName } from '@fortawesome/fontawesome-common-types'
import { Stack } from '@mui/material'
import { LeafCopyButton } from '@thriveglobal/thrive-web-leafkit'
import {
    ShareContentOnTeams,
    useIsMsTeams
} from '@thriveglobal/thrive-web-ms-teams-app'
import { Avo, AvoTypes } from '@thriveglobal/thrive-web-tracking'
import { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import FeedbackActions from '../ContentActions/FeedbackActions'
import { StatusPill } from '../StatusPill'
import { Card, CardDetails, CardHeader, CardStatus } from './'
import { CARD_WIDTH } from './Card'
import { useGenerateTeamsDeepLink } from '@thriveglobal/thrive-web-core'
import { useContentSharedTracking } from '../../../tracking'

const intlMessages = defineMessages({
    completed: {
        defaultMessage: 'Completed',
        description: `Something has been completed`
    },
    copyTitleToClipboard: {
        defaultMessage: 'Copy to clipboard {title}',
        description: 'Copy to clipboard - followed by the content title'
    },
    copyToClipboard: {
        defaultMessage: 'Copy to clipboard',
        description: `Aria label for compying a link to the clipboard`
    }
})

export type ContentCardProps = {
    id?: string
    title?: string
    subTitle?: string
    imageUrl?: string
    largeImage?: boolean
    contentStatus?: string
    icon?: IconName
    isComplete?: boolean
    progress?: number
    typeName?: string
    typeIcon?: IconName
    IconComponent?: React.ReactNode
    statusPillText?: string
    responsive?: boolean
    width?: CARD_WIDTH
    rootMargin?: string
    root?: null | Element
    showLoading?: boolean
    threshold?: number
    shareUrl?: string
    hasFeedbackActionsEnabled?: boolean
    pageSection?: AvoTypes.PageSectionValueType
    onClick?: () => void
    onEnter?: (entry: IntersectionObserverEntry) => void
}

export default function ContentCard({
    id,
    title,
    subTitle,
    imageUrl,
    largeImage,
    contentStatus,
    icon,
    isComplete,
    progress,
    typeName,
    typeIcon,
    IconComponent,
    statusPillText,
    responsive,
    width = CARD_WIDTH.medium,
    rootMargin,
    root,
    showLoading = false,
    threshold = 0.5,
    shareUrl,
    hasFeedbackActionsEnabled,
    pageSection,
    onClick,
    onEnter
}: ContentCardProps) {
    const intl = useIntl()
    const { isMsTeams } = useIsMsTeams()
    const generateTeamsDeepLink = useGenerateTeamsDeepLink()
    const trackContentShared = useContentSharedTracking()

    const activeShareUrl = useMemo(() => {
        if (isMsTeams && shareUrl) {
            return generateTeamsDeepLink(
                shareUrl.toString().substring(1)
            ).toString()
        }

        return `${window.location.origin}${shareUrl}`
    }, [isMsTeams, generateTeamsDeepLink, shareUrl])

    const onCopied = useCallback(() => {
        if (shareUrl && title && id) {
            trackContentShared({
                pageSection,
                activityType: 'url_copied',
                contentId: id,
                contentTitle: title,
                contentUrl: shareUrl,
                contentType: typeName as AvoTypes.ContentTypeValueType
            })
        }
    }, [shareUrl, title, id, trackContentShared, typeName, pageSection])

    const renderActionSection = () => {
        if (!shareUrl) return null

        return (
            <Stack
                direction={'row'}
                sx={{
                    whiteSpace: 'nowrap',
                    marginLeft: 'auto'
                }}
                gap={1}
                onClick={(e) => {
                    e.stopPropagation()
                }}
                onKeyDown={(e) => {
                    e.stopPropagation()
                }}
                role="group"
                aria-label="Content sharing actions"
            >
                {hasFeedbackActionsEnabled && (
                    <FeedbackActions
                        title={title}
                        onFeedback={(feedback) => {
                            if (id && title && typeName) {
                                Avo.reactionSubmitted({
                                    activityType: 'recommended_content_served',
                                    contentFormatType: null,
                                    contentId: id,
                                    contentSubtype: null,
                                    contentTitle: title,
                                    contentType:
                                        typeName as AvoTypes.ContentTypeValueType,
                                    contentUrl: null,
                                    conversationId: null,
                                    featureType: 'library',
                                    feedbackAnswer:
                                        feedback === true
                                            ? 'positive'
                                            : 'negative',
                                    feedbackQuestion: null,
                                    messageType: null,
                                    openAnswer: null,
                                    openQuestion: null,
                                    resetType: null,
                                    transactionId: null,
                                    contentSource: null,
                                    tabName: null,
                                    isSystemEvent: false
                                })
                            }
                        }}
                    />
                )}
                <LeafCopyButton
                    ariaLabel={
                        title
                            ? intl.formatMessage(
                                  intlMessages.copyTitleToClipboard,
                                  {
                                      title
                                  }
                              )
                            : intl.formatMessage(intlMessages.copyToClipboard)
                    }
                    icon="link"
                    linkBody={activeShareUrl}
                    noLabel
                    toolTipPosition="top"
                    onCopied={onCopied}
                />
                <ShareContentOnTeams
                    url={shareUrl}
                    renderMessageAsDeeplink={true}
                    onShared={onCopied}
                />
            </Stack>
        )
    }

    return (
        <Card
            id={id}
            width={width}
            responsive={responsive}
            rootMargin={rootMargin}
            root={root}
            showLoading={showLoading}
            detailsHeight={148}
            threshold={threshold}
            onClick={onClick}
            onEnter={onEnter}
            imageSection={
                <CardHeader
                    largeImage={largeImage}
                    imageSrc={imageUrl}
                    topLeftContent={
                        statusPillText &&
                        !isComplete && (
                            <StatusPill
                                label={statusPillText}
                                variant={progress ? 'primary' : 'info'}
                            />
                        )
                    }
                    progress={!isComplete ? progress : undefined}
                />
            }
            detailsSection={
                <CardDetails
                    title={title}
                    subTitle={subTitle}
                    topLeftContent={
                        <CardStatus
                            label={typeName}
                            iconName={typeIcon}
                            IconComponent={IconComponent}
                        />
                    }
                    topRightContent={
                        <CardStatus
                            label={
                                isComplete
                                    ? intl.formatMessage(intlMessages.completed)
                                    : contentStatus
                            }
                            iconName={isComplete ? 'check' : icon}
                            variant={isComplete ? 'success' : 'disabled'}
                        />
                    }
                    subTitleLineClamp={largeImage ? 3 : undefined}
                    bottomRightContent={renderActionSection()}
                />
            }
        />
    )
}
